/**
 * `/features/:slug`: one page per part of FactreeTech, written in
 * `content/features.ts`. The price is the plan's live price, inc GST.
 */

import { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import PublicLayout from '@/components/public/PublicLayout';
import MarketingShot from '@/components/public/MarketingShot';
import { hasShot } from '@/utils/marketingShots';
import { FEATURES, featureBySlug } from '@/content/features';
import { usePublicPlans, planFor } from '@/hooks/usePublicPlans';
import { activeTiers, formatPrice, incGst } from '@/utils/publicPricing';
import type { SubscriptionPlan } from '@/types/subscription';
import { cn } from '@/utils/cn';

function PlanPrice({ plan }: { plan: SubscriptionPlan }) {
  const base = incGst(plan.base_price);
  const tiers = activeTiers(plan);
  return (
    <div className="space-y-3">
      <p className="text-sm font-semibold text-neutral-500">{plan.name}</p>
      {base > 0 && (
        <p>
          <span className="text-3xl font-bold text-neutral-900">{formatPrice(base)}</span>
          <span className="text-neutral-600"> a month inc GST</span>
        </p>
      )}
      {tiers.length > 0 && (
        <ul className="space-y-1.5 text-sm">
          {tiers.map((t) => {
            const p = incGst(t.price_per_seat);
            return (
              <li key={t.id} className="flex justify-between gap-4">
                <span className="min-w-0 text-neutral-700">{t.name} seat</span>
                <span className="shrink-0 font-medium text-neutral-900">{p > 0 ? `${formatPrice(p)}/month inc GST` : 'Free'}</span>
              </li>
            );
          })}
        </ul>
      )}
      <p className="text-xs text-neutral-500">Prices include GST.</p>
    </div>
  );
}

export default function FeaturePage() {
  const { slug } = useParams<{ slug: string }>();
  const feature = featureBySlug(slug);
  const { plans, loading } = usePublicPlans();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (feature) document.title = `${feature.name} | FactreeTech`;
    return () => {
      document.title = 'FactreeTech';
    };
  }, [feature]);

  if (!feature) return <Navigate to="/" replace />;

  const plan = planFor(plans, feature.planKey);
  const [lead, ...rest] = feature.shots.filter((s) => hasShot(s.shot));
  const others = FEATURES.filter((f) => f.slug !== feature.slug);

  return (
    <PublicLayout>
      <section className="bg-gradient-to-b from-primary-50 via-white to-white">
        <div className="mx-auto max-w-7xl px-4 pb-14 pt-12 sm:px-6 lg:px-8 lg:pt-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_20rem]">
            <div className="min-w-0 space-y-5">
              <div className="flex items-center gap-3">
                <span className={cn('flex h-11 w-11 items-center justify-center rounded-xl', feature.tone)}>
                  <feature.icon className="h-6 w-6" />
                </span>
                <span className="text-sm font-semibold uppercase tracking-wide text-neutral-500">{feature.name}</span>
              </div>
              <h1 className="text-4xl font-bold tracking-tight text-neutral-900 sm:text-5xl">{feature.headline}</h1>
              <p className="text-lg leading-8 text-neutral-600">{feature.intro}</p>
              <p className="text-sm text-neutral-500">
                <span className="font-semibold text-neutral-700">For:</span> {feature.forWhom}
              </p>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Link to="/register" className="rounded-xl bg-primary-600 px-6 py-3 text-center font-semibold text-white hover:bg-primary-700">
                  Get started free
                </Link>
                <Link to="/pricing" className="rounded-xl border border-neutral-300 bg-white px-6 py-3 text-center font-semibold text-neutral-800 hover:bg-neutral-50">
                  All pricing
                </Link>
              </div>
            </div>

            <aside className="h-fit rounded-2xl border border-neutral-200 bg-white p-6 shadow-soft">
              {feature.planKey ? (
                plan ? (
                  <PlanPrice plan={plan} />
                ) : (
                  <p className="text-sm text-neutral-600">
                    {loading ? 'Loading prices…' : <>See <Link to="/pricing" className="font-semibold text-primary-700">pricing</Link> for what it costs.</>}
                  </p>
                )
              ) : (
                <p className="text-2xl font-bold text-emerald-700">Included</p>
              )}
              {feature.freeNote && (
                <p className="mt-4 border-t border-neutral-100 pt-4 text-sm text-neutral-700">{feature.freeNote}</p>
              )}
            </aside>
          </div>

          {lead && <MarketingShot name={lead.shot} alt={lead.caption} caption eager className="mt-14" />}
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-neutral-900 sm:text-3xl">What it does</h2>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {feature.highlights.map((h) => (
              <div key={h.title} className="rounded-2xl border border-neutral-200 p-6">
                <Check className="h-6 w-6 text-primary-600" />
                <h3 className="mt-3 font-semibold text-neutral-900">{h.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600">{h.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {rest.length > 0 && (
        <section className="bg-neutral-50 py-16">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
            {rest.map((s) => (
              <MarketingShot key={s.shot} name={s.shot} alt={s.caption} caption />
            ))}
          </div>
        </section>
      )}

      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-neutral-900">Works with the rest of FactreeTech</h2>
          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {others.map((f) => (
              <Link key={f.slug} to={`/features/${f.slug}`} className="flex min-w-0 items-center gap-3 rounded-xl border border-neutral-200 p-3 hover:border-primary-300 hover:bg-neutral-50">
                <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', f.tone)}>
                  <f.icon className="h-5 w-5" />
                </span>
                <span className="min-w-0 text-sm font-medium text-neutral-900">{f.name}</span>
                <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-neutral-400" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
